/* ============================================================
   The sky — the desktop's signature surface. A band of gradient the Today
   header sits on, whose colour follows the CLOCK (dawn → day → dusk → night,
   blended by the minute so no two visits look alike) and whose sun follows
   the DAY'S PROGRESS: it peeks over the horizon on an untouched morning and
   stands clear of it when everything is done, at which point it earns the
   brand mark's rays. Progress rendered as light, not as a number — the
   consistency-not-streak stance (CLAUDE.md) in a picture. A quiet day leaves
   the sun low; nothing is ever "lost".

   Also the sign-in scene (`variant="scene"`), full-height, with the sun
   rising on mount.
   ============================================================ */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useHtmlScheme } from '../theme/ThemeProvider';

type Moment = 'night' | 'dawn' | 'day' | 'dusk';

/* The day as keyframes (hour → moment); between two keys the sky is a blend
   of their moments, weighted linearly. */
const KEYS: [number, Moment][] = [[0, 'night'], [5, 'night'], [7, 'dawn'], [10.5, 'day'], [16.5, 'day'], [19.5, 'dusk'], [21.5, 'night'], [24, 'night']];

export interface SkyBlend { a: Moment; b: Moment; w: number; night: number; day: number }

export function blendForHour(h: number): SkyBlend {
  let i = 0;
  while (i < KEYS.length - 2 && h >= KEYS[i + 1]![0]) i++;
  const [h0, a] = KEYS[i]!;
  const [h1, b] = KEYS[i + 1]!;
  const w = Math.min(1, Math.max(0, (h - h0) / (h1 - h0)));
  const weight = (m: Moment) => (a === m ? 1 - w : 0) + (b === m ? w : 0);
  return { a, b, w, night: weight('night'), day: weight('day') };
}

function useClockBlend(fixedHour?: number): SkyBlend {
  const [hour, setHour] = useState(() => fixedHour ?? nowHour());
  useEffect(() => {
    if (fixedHour != null) return;
    const t = setInterval(() => setHour(nowHour()), 60_000);
    return () => clearInterval(t);
  }, [fixedHour]);
  return useMemo(() => blendForHour(hour), [hour]);
}
const nowHour = () => { const d = new Date(); return d.getHours() + d.getMinutes() / 60; };

/* a seeded scatter of stars, stable across renders */
function stars(n: number, seed: number) {
  let s = seed;
  const rnd = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  return Array.from({ length: n }, (_, i) => ({ id: i, x: rnd() * 100, y: 4 + rnd() * 72, r: 0.6 + rnd() * 1.1, dur: 2.4 + rnd() * 3, delay: rnd() * 4 }));
}
const STARS = stars(46, 7);

export function Sky({
  progress, variant = 'band', hour, children, className = '', height = 268, sunTitle,
}: {
  /** 0..1 — how much of the day is done; the sun's height */
  progress: number;
  variant?: 'band' | 'scene';
  /** pin the clock (previews, tests); otherwise live */
  hour?: number;
  children?: React.ReactNode;
  className?: string;
  height?: number;
  /** a hover hint on the sun, so what its height means is one pointer away */
  sunTitle?: string;
}) {
  const blend = useClockBlend(hour);
  const scheme = useHtmlScheme();
  const p = Math.min(1, Math.max(0, progress));

  const mix = (facet: 'top' | 'mid' | 'horizon') =>
    `color-mix(in oklab, var(--sky-${blend.a}-${facet}) ${Math.round((1 - blend.w) * 100)}%, var(--sky-${blend.b}-${facet}))`;

  // The viewBox follows the box's own aspect (measured, so a 390px phone band
  // and a 1500px desktop band each get a sky that fits with nothing cropped
  // and the sun where it was placed). 1 px = 1000 / width units, which is
  // what lets the sun be sized in pixels.
  const box = useRef<HTMLDivElement>(null);
  const [aspect, setAspect] = useState(variant === 'scene' ? 1 : 0.24);
  const [widthPx, setWidthPx] = useState(variant === 'scene' ? 900 : 1400);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) { setAspect(height / width); setWidthPx(width); }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const W = 1000;
  const H = Math.round(W * aspect);
  const unit = W / widthPx; // viewBox units per CSS px
  const squat = aspect < 0.6; // a short wide band (Today's header, the phone's sign-in band)
  // the sun: sized in pixels, capped so a short band never drowns in it,
  // placed on the right where the header leaves the sky empty (centre-right
  // on the tall sign-in scene)
  const r = Math.min((variant === 'scene' ? 96 : 76) * unit, H * 0.3);
  const sunX = variant === 'scene' && !squat ? W * 0.66 : squat && widthPx < 768 ? W * 0.8 : W * 0.86;
  // the sun's path: from a sliver at the horizon to clear of it
  const low = H + r * 0.3;
  const high = H * 0.4;
  const sunY = low - (low - high) * easeOut(p);
  const risen = p >= 0.999;
  // at night the sun cools to a moon; by day it is the brand's amber
  const night = blend.night;
  const core = night > 0.5 ? '#fff9e8' : 'var(--sun-core)';
  // ink for whatever is painted on the sky: white once the ground is dark
  const darkGround = scheme === 'dark' || night > 0.45;
  const inkVars = darkGround
    ? { '--sky-ink': 'rgba(255,255,255,0.96)', '--sky-ink-2': 'rgba(255,255,255,0.72)', '--sky-ink-3': 'rgba(255,255,255,0.5)' }
    : { '--sky-ink': 'var(--ink)', '--sky-ink-2': 'var(--ink-2)', '--sky-ink-3': 'var(--meta)' };
  const edge = `color-mix(in oklab, var(--sun-edge) ${Math.round((1 - night) * 100)}%, #cfc3f0)`;

  return (
    <div
      ref={box}
      className={`relative overflow-hidden ${className}`}
      style={{
        ...(inkVars as React.CSSProperties),
        height: variant === 'scene' ? '100%' : height,
        background: `linear-gradient(180deg, ${mix('top')} 0%, ${mix('mid')} 58%, ${mix('horizon')} 100%)`,
        transition: 'background 1.2s linear',
      }}
    >
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMax slice" aria-hidden>
        <defs>
          {/* the disc: lit from the upper left, so it reads as a sphere, not a dot */}
          <radialGradient id="sky-sun" cx="0.38" cy="0.32" r="0.78">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="0.3" stopColor={core} />
            <stop offset="0.8" stopColor={core} stopOpacity="0.97" />
            <stop offset="1" stopColor={edge} />
          </radialGradient>
          {/* its light, in three layers so the falloff is smooth rather than a
             single stepped disc: a tight blurred bloom at the rim, a halo with
             a gentle (near-gaussian) tail, and a very wide, very faint ambient
             that tints the sky around it */}
          <radialGradient id="sky-bloom">
            <stop offset="0" stopColor={core} stopOpacity="0.9" />
            <stop offset="0.55" stopColor={edge} stopOpacity="0.5" />
            <stop offset="1" stopColor={edge} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="sky-halo">
            <stop offset="0" stopColor={edge} stopOpacity="0.34" />
            <stop offset="0.3" stopColor={edge} stopOpacity="0.18" />
            <stop offset="0.6" stopColor={edge} stopOpacity="0.06" />
            <stop offset="1" stopColor={edge} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="sky-ambient">
            <stop offset="0" stopColor={edge} stopOpacity="0.16" />
            <stop offset="0.5" stopColor={edge} stopOpacity="0.05" />
            <stop offset="1" stopColor={edge} stopOpacity="0" />
          </radialGradient>
          <filter id="sky-blur" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation={22 * unit} /></filter>
          <filter id="sky-soft" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation={9 * unit} /></filter>
          <clipPath id="sky-above"><rect x="0" y="-200" width={W} height={H + 200} /></clipPath>
        </defs>

        {/* stars: present by the night weight; a few linger at dawn and dusk */}
        <g style={{ opacity: night * 0.95, transition: 'opacity 1.2s linear' }}>
          {STARS.map((s) => (
            <circle key={s.id} className="star" cx={(s.x / 100) * W} cy={(s.y / 100) * H} r={s.r * unit * 1.1} fill="#fff"
              style={{ ['--dur' as string]: `${s.dur}s`, ['--delay' as string]: `${s.delay}s` }} />
          ))}
        </g>

        {/* two soft clouds, daytime only, drifting */}
        <g style={{ opacity: blend.day * (scheme === 'dark' ? 0.18 : 0.7), transition: 'opacity 1.2s linear' }} filter="url(#sky-blur)">
          <ellipse className="cloud" cx={W * 0.22} cy={H * 0.42} rx={190} ry={Math.min(H * 0.09, 40 * unit)} fill="#fff" />
          <ellipse className="cloud" cx={W * 0.6} cy={H * 0.26} rx={150} ry={Math.min(H * 0.07, 32 * unit)} fill="#fff" style={{ animationDelay: '-18s' }} />
        </g>

        <g clipPath="url(#sky-above)">
          {/* halo, breathing */}
          {/* the scene's sun rises from the horizon on mount (tokens.css .sun-rise);
             the band's moves only when progress does */}
          <g
            className={variant === 'scene' ? 'sun-rise' : undefined}
            style={{ transform: `translate(${sunX}px, ${sunY}px)`, ['--sun-low' as string]: `translate(${sunX}px, ${low}px)`, transition: 'transform 1.4s var(--ease-out)' }}
          >
            <circle className="sun-ambient" r={r * 5.5} fill="url(#sky-ambient)" />
            <circle className="sun-glow" r={r * 3.2} fill="url(#sky-halo)" />
            <circle className="sun-bloom" r={r * 1.5} fill="url(#sky-bloom)" filter="url(#sky-soft)" />
            <circle r={r} fill="url(#sky-sun)" style={{ pointerEvents: 'auto' }}>{sunTitle ? <title>{sunTitle}</title> : null}</circle>
            {/* a hairline rim: the one crisp edge in all that light */}
            <circle r={r} fill="none" stroke="#fff" strokeOpacity={0.5} strokeWidth={1.25 * unit} />
            {/* the rays — the brand mark's five, plus their mirror — only once the day is done */}
            <g stroke={core} strokeWidth={r * 0.14} strokeLinecap="round" style={{ opacity: risen ? 1 : 0, transition: 'opacity 0.9s ease 0.4s' }}>
              {Array.from({ length: 10 }, (_, i) => {
                const a = -Math.PI + (i / 9) * Math.PI; // the upper half, horizon to horizon
                const in1 = r * 1.3, out1 = r * 1.62;
                return <line key={i} x1={Math.cos(a) * in1} y1={Math.sin(a) * in1} x2={Math.cos(a) * out1} y2={Math.sin(a) * out1} />;
              })}
            </g>
          </g>
        </g>

        {/* the horizon: a soft pool of the sun's light on the line, strongest
           while the sun sits low, and the brand mark's warm hairline over it */}
        <ellipse cx={sunX} cy={H} rx={r * 2.6} ry={r * 0.32} fill={edge} filter="url(#sky-blur)"
          style={{ opacity: 0.18 + 0.42 * (1 - easeOut(p)), transition: 'opacity 1.4s var(--ease-out)' }} />
        <line x1={0} x2={W} y1={H - unit} y2={H - unit} stroke={edge} strokeOpacity={0.45} strokeWidth={1.5 * unit} />
      </svg>

      <div className="relative z-[1] h-full">{children}</div>
    </div>
  );
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 2.2);
